import React from 'react'
import { inject, observer } from 'mobx-react'
import { CreateProduct, CreateChildProduct, MetaDataReservedKeys } from '../ProductStore'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { UncontrolledNumberInput } from '../../../../../components/NumberInput'
import { ResponsiveRow } from '../../../../../components/ResponsiveContainer/ResponsiveRow'
import { ColProps } from 'antd/lib/grid'
import { Select, Col } from 'antd'
import { ProductModels } from '@getgreenline/products'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  product: CreateProduct | CreateChildProduct
}

@inject('currentCompanyStore')
@observer
export class Details extends React.Component<Props> {
  company = this.props.currentCompanyStore!.company!
  canUseGlobalCatalog = this.props.currentCompanyStore!.canUseGlobalCatalog

  constructor(props: Props) {
    super(props)
  }

  get getNetProductWeightDescription() {
    return this.company.province === 'SK' ? 'Required' : 'Optional'
  }

  netContentUnits = ['g', 'mg', 'ml', 'each', 'caps', 'cm', 'oz', 'pack']

  strainTypes: ProductModels.EProductStrainType[] = [
    ProductModels.EProductStrainType.BLEND,
    ProductModels.EProductStrainType.BALANCED,
    ProductModels.EProductStrainType.CBD,
    ProductModels.EProductStrainType.HYBRID,
    ProductModels.EProductStrainType.INDICA,
    ProductModels.EProductStrainType.INDICA_DOMINANT,
    ProductModels.EProductStrainType.SATIVA,
    ProductModels.EProductStrainType.SATIVA_DOMINANT,
  ]

  render() {
    const { product } = this.props
    const { metaData } = product
    const rowProps = { style: { marginBottom: 16, minWidth: '100%' } }
    const labelColProps: ColProps = { md: 24, lg: 10 }
    const contentColProps: ColProps = { md: 24, lg: 14 }

    const isDeleted =
      product instanceof CreateProduct ? product.isDeleted : product.parentProduct.isDeleted

    return (
      <div>
        <ResponsiveRow
          labelText={
            <div>
              <strong>Cannabis weight</strong>
              <div className='text-muted'>Equivalent dried cannabis weight</div>
            </div>
          }
          rowProps={rowProps}
          labelColProps={labelColProps}
          contentColProps={contentColProps}
        >
          <div style={{ width: 100 }}>
            <UncontrolledNumberInput
              addonAfter='g'
              value={product.cannabisWeight}
              disabled={isDeleted}
              onChange={(newValue) => product.setCannabisWeight(newValue)}
            />
          </div>
        </ResponsiveRow>

        <ResponsiveRow
          labelText={
            <div>
              <strong>Net product weight</strong>
              <div className='text-muted'>{this.getNetProductWeightDescription}</div>
            </div>
          }
          rowProps={rowProps}
          labelColProps={labelColProps}
          contentColProps={contentColProps}
        >
          <div style={{ width: 100 }}>
            <UncontrolledNumberInput
              addonAfter='g'
              value={product.weight}
              disabled={isDeleted}
              onChange={(newValue) => product.setWeight(newValue)}
            />
          </div>
        </ResponsiveRow>

        <ResponsiveRow
          labelText={
            <div>
              <strong>Cannabis volume</strong>
              <div className='text-muted'>Optional</div>
            </div>
          }
          rowProps={rowProps}
          labelColProps={labelColProps}
          contentColProps={contentColProps}
        >
          <div style={{ width: 100 }}>
            <UncontrolledNumberInput
              addonAfter='ml'
              disabled={!!product.pricingGroupId || isDeleted}
              value={product.cannabisVolume}
              onChange={(newValue) => product.setCannabisVolume(newValue)}
            />
          </div>
        </ResponsiveRow>
        {this.canUseGlobalCatalog && (
          <>
            <ResponsiveRow
              labelText={
                <div>
                  <strong>Quantity</strong>
                  <div className='text-muted'>Optional</div>
                </div>
              }
              rowProps={{ style: { marginBottom: 2, minWidth: '100%' } }}
              labelColProps={labelColProps}
              contentColProps={contentColProps}
            >
              <div style={{ width: 100 }}>
                <UncontrolledNumberInput
                  disabled={isDeleted}
                  value={metaData.quantity}
                  onChange={(newValue: number | null) =>
                    metaData.setValue(MetaDataReservedKeys.QUANTITY, newValue)
                  }
                />
              </div>
            </ResponsiveRow>
            <ResponsiveRow
              labelText={
                <div>
                  <strong>Net content</strong>
                  <div className='text-muted'>Optional</div>
                </div>
              }
              rowProps={rowProps}
              labelProps={{ className: 'mt-3' }}
              labelColProps={labelColProps}
              contentColProps={contentColProps}
            >
              <div>
                <div className='d-flex flex-row flex-wrap'>
                  <div className={'mr-2'} style={{ width: 100, marginTop: 1 }}>
                    <label></label>
                    <UncontrolledNumberInput
                      className='d-flex justify-content-center'
                      disabled={isDeleted}
                      value={metaData.netContent}
                      onChange={(newValue: number | null) =>
                        metaData.setValue(MetaDataReservedKeys.NET_CONTENT, newValue)
                      }
                    />
                  </div>

                  <div className={'mr-2'}>
                    <label style={{ margin: 0 }}>Unit</label>
                    <Select<string | null>
                      showSearch
                      className='show-close-icon'
                      size='default'
                      style={{ width: '100%' }}
                      dropdownMatchSelectWidth={false}
                      value={metaData.netContentUnit}
                      disabled={product.isDeleted}
                      allowClear={metaData.netContentUnit !== null}
                      onChange={(netContentUnit) => {
                        metaData.setValue(MetaDataReservedKeys.NET_CONTENT_UNIT, netContentUnit)
                      }}
                    >
                      {this.netContentUnits.map((netContentUnit, index) => (
                        <Select.Option key={index} value={netContentUnit}>
                          {netContentUnit}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            </ResponsiveRow>
          </>
        )}
        <ResponsiveRow
          labelText={
            <div>
              <strong>Strain type</strong>
              <div className='text-muted'>Optional</div>
            </div>
          }
          rowProps={{ style: { ...rowProps.style, marginBottom: '40%' } }}
          labelColProps={{ md: 24, lg: 6 }}
          contentColProps={{ md: 12, lg: 18 }}
        >
          <div style={{ width: 100 }}>
            <Select<ProductModels.EProductStrainType | null>
              showSearch
              className='ml-2 show-close-icon'
              style={{ width: '200%' }}
              size='default'
              dropdownMatchSelectWidth={false}
              value={product.strainType}
              disabled={product.isDeleted}
              allowClear={product.strainType !== null}
              onChange={(strainType) => {
                product.setStrainType(strainType)
              }}
            >
              {this.strainTypes.map((strainType, index) => (
                <Select.Option key={index} value={strainType}>
                  {strainType}
                </Select.Option>
              ))}
            </Select>
          </div>
        </ResponsiveRow>
      </div>
    )
  }
}
